<script>
/**
 * SummaryStatCard -- the stat-card unit for the role dashboard headers.
 *
 * Anatomy: caption label -> big tabular value -> optional detail/trend.
 * Used to build the summary row of the shared DashboardLayout skeleton; the
 * card's *data* is a Story 1.4/1.5 concern -- this is the presentational shell.
 *
 * Color comes from the Story 1.1 design tokens (:root CSS custom properties) --
 * never hardcode hexes (AC-8). Tone is never color-only: `tone="warning"` adds a
 * non-color cue (a visible mdi glyph + a "Warning" affordance in the accessible
 * name) so a grayscale / color-vision-deficient user can still tell it apart
 * (AC-2/3, UX-DR16 "never color alone").
 */
const TONES = ["neutral", "warning"];

export default {
  name: "SummaryStatCard",
  props: {
    label: {
      type: String,
      required: true
    },
    value: {
      type: [String, Number],
      required: true
    },
    detail: {
      type: String,
      default: ""
    },
    tone: {
      type: String,
      default: "neutral",
      validator: value => TONES.indexOf(value) !== -1
    }
  },
  computed: {
    // Guard against an out-of-enum tone: the prop validator only warns in dev
    // and is stripped in production. Clamp once, use everywhere (StatusPill pattern).
    normalizedTone() {
      return TONES.indexOf(this.tone) !== -1 ? this.tone : "neutral";
    },
    isWarning() {
      return this.normalizedTone === "warning";
    },
    // Single accessible name so a screen reader announces the card as one unit,
    // e.g. "Awaiting invoice, 12, 3 overdue" (and "Warning, ..." when tone is
    // warning -- the tone meaning is carried in real text, never colour alone).
    // The visual children are aria-hidden so this name is read once, atomically.
    accessibleName() {
      const parts = [];
      if (this.isWarning) {
        parts.push("Warning");
      }
      parts.push(this.label);
      parts.push(String(this.value));
      if (this.detail) {
        parts.push(this.detail);
      }
      return parts.join(", ");
    }
  }
};
</script>

<template>
  <div
    :class="['summary-stat-card', `summary-stat-card--${normalizedTone}`]"
    role="img"
    :aria-label="accessibleName"
  >
    <div class="summary-stat-card__body" aria-hidden="true">
      <div class="summary-stat-card__head">
        <span class="summary-stat-card__label">{{ label }}</span>
        <i
          v-if="isWarning"
          class="mdi mdi-alert-outline summary-stat-card__icon"
        ></i>
      </div>
      <div class="summary-stat-card__value tabular-nums">{{ value }}</div>
      <div v-if="detail" class="summary-stat-card__detail">{{ detail }}</div>
    </div>
  </div>
</template>

<style scoped>
/* Card chrome from tokens only -- re-points with the brand palette (AC-8). */
.summary-stat-card {
  background: var(--c-surface);
  border: 1px solid var(--c-border);
  border-radius: var(--radius-card);
  box-shadow: var(--shadow-card);
  padding: var(--space-6);
}

.summary-stat-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
}

.summary-stat-card__label {
  font-size: var(--t-caption); /* 12px */
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--c-text-muted);
}

/* Big scannable value -- tabular-nums (global utility, Story 1.1) keeps
   numerals aligned. Weight set here: _tokens.scss carries sizes, not weights. */
.summary-stat-card__value {
  margin-top: var(--space-2);
  font-size: var(--t-h1);
  font-weight: 600;
  line-height: 1.1;
  color: var(--c-text);
  /* A long/unbroken value must wrap inside the card instead of overflowing the
     column and breaking the grid row (the column also carries min-width:0). */
  overflow-wrap: break-word;
}

.summary-stat-card__detail {
  margin-top: var(--space-1);
  font-size: var(--t-small);
  font-weight: 400;
  color: var(--c-text-muted);
}

/* Warning icon: the non-color cue. Colour is only reinforcement (AC-2). */
.summary-stat-card__icon {
  flex: none;
  font-size: var(--t-h3);
  line-height: 1;
}
.summary-stat-card--warning .summary-stat-card__icon {
  color: var(--c-warning);
}
</style>
